import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetTournamentsQuery, PlayStatus } from '@slope/graphql/apollo';
import type { GetTournamentsQuery } from '@slope/graphql/apollo';
import { Flex, Typography, Button, Box } from '@slope/uikit';
import styled from 'styled-components';
import { Search } from '@carbon/icons-react';

type TournamentFromQuery = NonNullable<GetTournamentsQuery['tournaments']>['nodes'][0];

const StyledContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
`;

const TournamentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const TournamentCard = styled.div`
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 280px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: #007bff;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 500px;
  margin: 2rem auto;
`;

const SearchInput = styled.input`
  padding: 12px 16px 12px 3rem;
  width: 100%;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #007bff;
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  z-index: 1;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadMoreTrigger = styled.div`
  height: 20px;
  margin: 2rem 0;
`;

const LoadingMore = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const ContentLoadingOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
`;

export const HomeView: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  const { data, loading, error, fetchMore } = useGetTournamentsQuery({
    variables: {
      filters: { statuses: [PlayStatus.InProgress], name: searchQuery || null },
      pagination: { first: 20 },
    },
    notifyOnNetworkStatusChange: true,
  });

  const [loadingMore, setLoadingMore] = useState(false);

  // Track if we've loaded data at least once
  useEffect(() => {
    if (data && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [data, hasInitiallyLoaded]);

  // Transform GraphQL data
  const tournaments: TournamentFromQuery[] = useMemo(() => {
    return data?.tournaments?.nodes || [];
  }, [data]);

  const handleTournamentSelect = (tournamentId: string) => {
    navigate(`/tournament/${tournamentId}/live`, {
      viewTransition: true,
    });
  };

  // Infinite scroll handler
  const handleLoadMore = useCallback(async () => {
    const pageInfo = data?.tournaments?.pageInfo;
    if (!pageInfo?.hasNextPage || loadingMore || loading) {
      return;
    }

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          filters: { statuses: [PlayStatus.InProgress] },
          pagination: {
            first: 20,
            after: pageInfo.endCursor,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            tournaments: {
              ...fetchMoreResult.tournaments,
              nodes: [...prev.tournaments.nodes, ...fetchMoreResult.tournaments.nodes],
            },
          };
        },
      });
    } catch (err) {
      console.error('Error loading more tournaments:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [data, fetchMore, loading, loadingMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore]);

  // Show full-page spinner only on initial load
  if (loading && !hasInitiallyLoaded) {
    return (
      <StyledContainer>
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: '50vh' }}
        >
          <LoadingSpinner />
          <Typography fontSize="18px" fontWeight="600" mt="1rem">
            Cargando torneos...
          </Typography>
        </Flex>
      </StyledContainer>
    );
  }

  if (error) {
    return (
      <StyledContainer>
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: '50vh' }}
        >
          <Typography fontSize="24px" fontWeight="bold" color="red">
            Error
          </Typography>
          <Typography fontSize="16px" mt="0.5rem">
            Algo salió mal. Por favor, inténtalo de nuevo más tarde.
          </Typography>
        </Flex>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Flex direction="column" alignItems="center">
        <Typography fontSize="48px" fontWeight="bold" mb="1rem" color="#2c3e50" textAlign="center">
          Torneos en Curso
        </Typography>
        <Typography fontSize="18px" mb="2rem" color="#7f8c8d" textAlign="center">
          Explora torneos de golf en vivo
        </Typography>

        <SearchContainer>
          <SearchIcon size={20} />
          <SearchInput
            placeholder="Buscar por nombre, club, país..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>

        {loading && hasInitiallyLoaded && !loadingMore ? (
          <ContentLoadingOverlay>
            <LoadingSpinner />
            <Typography fontSize="16px" ml="1rem">
              Buscando torneos...
            </Typography>
          </ContentLoadingOverlay>
        ) : tournaments.length === 0 ? (
          <EmptyState>
            <Typography fontSize="24px" fontWeight="600" color="#95a5a6">
              No se encontraron torneos
            </Typography>
            <Typography fontSize="16px" mt="0.5rem" color="#bdc3c7">
              {searchQuery.trim()
                ? 'Intenta ajustar tus criterios de búsqueda'
                : 'No hay torneos en curso en este momento'}
            </Typography>
          </EmptyState>
        ) : (
          <>
            <TournamentGrid>
              {tournaments.map((tournament) => (
                <TournamentCard
                  key={tournament.id}
                  onClick={() => handleTournamentSelect(tournament.id)}
                >
                  <Box p="1.5rem">
                    <Typography fontSize="20px" fontWeight="bold" mb="0.5rem" color="#2c3e50">
                      {tournament.name}
                    </Typography>
                    <Typography fontSize="14px" color="#7f8c8d" mb="0.5rem">
                      🏌️ {tournament.host.name}
                    </Typography>
                    <Typography fontSize="14px" color="#7f8c8d" mb="2rem">
                      📍 {tournament.host.country}
                    </Typography>
                    {tournament.organizer.id !== tournament.host.id && (
                      <Typography fontSize="12px" color="#95a5a6" mb="2rem">
                        Organizado por: {tournament.organizer.name}
                      </Typography>
                    )}
                    <Button
                      fullWidth
                      //as={Link}
                      //to={`/tournament/${tournament.id}/live`}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleTournamentSelect(tournament.id);
                      }}
                    >
                      Ver Torneo
                    </Button>
                  </Box>
                </TournamentCard>
              ))}
            </TournamentGrid>

            {/* Infinite scroll trigger */}
            {data?.tournaments?.pageInfo?.hasNextPage && (
              <>
                <LoadMoreTrigger ref={observerTarget} />
                {loadingMore && (
                  <LoadingMore>
                    <LoadingSpinner />
                    <Typography fontSize="16px" ml="1rem">
                      Cargando más torneos...
                    </Typography>
                  </LoadingMore>
                )}
              </>
            )}
          </>
        )}
      </Flex>
    </StyledContainer>
  );
};
